'use client';

import { useEffect, useState } from 'react';
import { formatInt } from '@/lib/formatDate';
import s from './IndustryCommandCentre.module.css';

/**
 * IndustryCommandCentre — the shared "live dashboard" hero visual, generalized
 * from TelecomCommandCentre so every industry page can show its own sector
 * command-centre on the right of the hero (matching the Telecom benchmark).
 *
 * All content is passed in as data, so each industry is bespoke while the shell,
 * motion, and design language stay identical across the site.
 *
 * Props:
 *   ariaLabel    accessible name for the card
 *   pulseTitle   small heading top-right (e.g. "Network & subscriber pulse")
 *   liveLabel    the "LIVE" tag text (default "LIVE")
 *   kpis         [{ val, lbl, live? }] — exactly 3. One may set live:true to tick up.
 *   sentiment    [{ lbl, pct, tone }] — tone: 'positive' | 'neutral' | 'negative'
 *   sla          { pct, lbl }  — donut value + caption
 *   tickerLabel  ticker heading (e.g. "Incoming complaints")
 *   tickerItems  [{ letter, text, handle, tag, tagLevel }]  tagLevel: 'high'|'med'|undefined
 *   chips        [{ title, sub, variant }]  variant: 'light' | 'dark'
 */

const TONE_COLOR = {
  positive: 'var(--status-success-dot)',
  neutral: 'var(--text-muted)',
  negative: 'var(--danger, #E8543E)',
};

const CIRC = 188.5; // 2πr, r=30

function tagClass(level) {
  if (level === 'high') return `${s.tag} ${s.tagHigh}`;
  if (level === 'med') return `${s.tag} ${s.tagMed}`;
  return s.tag;
}

export default function IndustryCommandCentre({
  ariaLabel,
  pulseTitle,
  liveLabel = 'LIVE',
  kpis = [],
  sentiment = [],
  sla,
  tickerLabel,
  tickerItems = [],
  chips = [],
}) {
  const liveIndex = kpis.findIndex((k) => k.live);
  const liveSeed = liveIndex >= 0 ? parseInt(String(kpis[liveIndex].val).replace(/[^\d]/g, ''), 10) || 0 : 0;
  const [liveVal, setLiveVal] = useState(liveSeed);

  useEffect(() => {
    if (liveIndex < 0) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = window.setInterval(() => {
      setLiveVal((n) => n + Math.floor(Math.random() * 4) + 1);
    }, 1900);
    return () => window.clearInterval(id);
  }, [liveIndex]);

  const offset = sla ? Math.max(0, CIRC * (1 - sla.pct / 100)) : 47;

  return (
    <div className={s.wrap}>
      <article className={s.card} aria-label={ariaLabel}>
        <header className={s.cardHead}>
          <span className={s.liveDot} aria-hidden="true" />
          <span className={s.liveTag}>{liveLabel}</span>
          <span className={s.cardTitle}>{pulseTitle}</span>
        </header>

        <div className={s.kpiRow}>
          {kpis.map((k, i) => (
            <div key={k.lbl} className={s.kpi}>
              <span className={s.kpiVal}>
                {i === liveIndex ? formatInt(liveVal) : k.val}
              </span>
              <span className={s.kpiLbl}>{k.lbl}</span>
            </div>
          ))}
        </div>

        <div className={s.sentimentRow}>
          <div className={s.bars}>
            {sentiment.map((b) => (
              <div key={b.lbl} className={s.barRow}>
                <span className={s.barLbl}>{b.lbl}</span>
                <div className={s.barTrack}>
                  <div
                    className={`${s.barFill} ${s.barGrow}`}
                    style={{ '--bar-w': `${b.pct}%`, background: TONE_COLOR[b.tone] || 'var(--brand)' }}
                  />
                </div>
              </div>
            ))}
          </div>

          {sla && (
            <div className={s.donutWrap} aria-hidden="true">
              <svg viewBox="0 0 80 80" className={s.donut}>
                <circle cx="40" cy="40" r="30" fill="none" stroke="var(--border-light)" strokeWidth="8" />
                <circle
                  className={s.donutArc}
                  cx="40" cy="40" r="30" fill="none"
                  stroke="var(--brand)" strokeWidth="8"
                  strokeDasharray={CIRC}
                  strokeLinecap="round"
                  transform="rotate(-90 40 40)"
                  style={{ '--icc-offset': offset }}
                />
              </svg>
              <span className={s.donutVal}>{sla.pct}%</span>
              <span className={s.donutLbl}>{sla.lbl}</span>
            </div>
          )}
        </div>

        <div className={s.tickerWrap}>
          <p className={s.tickerLabel}>{tickerLabel}</p>
          <div className={s.tickerViewport}>
            <ul className={s.ticker}>
              {[...tickerItems, ...tickerItems].map((item, i) => (
                <li key={i} className={s.tickerItem}>
                  <span className={s.avatar}>{item.letter}</span>
                  <div className={s.tickerText}>
                    <span className={s.tickerMsg}>{item.text}</span>
                    <span className={s.tickerHandle}>{item.handle}</span>
                  </div>
                  <span className={tagClass(item.tagLevel)}>{item.tag}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </article>

      {chips.map((c) => (
        <div
          key={c.title}
          className={c.variant === 'dark' ? s.chipDark : s.chipLight}
          aria-hidden="true"
        >
          <span className={s.chipTitle}>{c.title}</span>
          <span className={s.chipSub}>{c.sub}</span>
        </div>
      ))}
    </div>
  );
}
